import { environment } from '@/constants/environment'
import { getAuthToken } from './api'
import { HTTP_STATUS } from '@/constants'
import { isInteractivePreviewMode } from '@/utils/previewMode'

// The websocket and the pending action live outside Zustand because both hold
// non-serializable refs (live socket, resolve/reject closures, timer ids) that
// can't be persisted or copied through immer drafts.

let ws: WebSocket | null = null

interface PendingAction {
  resolve: ((value: any) => void) | null
  reject: ((reason: any) => void) | null
  timeoutId: ReturnType<typeof setTimeout> | null
}

const pendingAction: PendingAction = {
  resolve: null,
  reject: null,
  timeoutId: null,
}

// Set after the message handler is registered. Lets the WS plumbing dispatch a
// validated `session` payload back into the store without a circular import.
let onSessionMessage: ((data: any, status: number) => void) | null = null

export function setSessionMessageHandler(handler: (data: any, status: number) => void) {
  onSessionMessage = handler
}

// Injected like onSessionMessage: importing the UI store here would close the cycle
// useUIStore -> useSessionStore -> websocket and leave this module half-initialized.
let onSocketError: ((payload: { response: any }) => void) | null = null

export function setSocketErrorHandler(handler: (payload: { response: any }) => void) {
  onSocketError = handler
}

function clearPendingTimeout() {
  if (pendingAction.timeoutId) {
    clearTimeout(pendingAction.timeoutId)
    pendingAction.timeoutId = null
  }
}

export function setPendingAction(action: Omit<PendingAction, never>) {
  // There is one slot, so an unsettled predecessor has to be rejected here. Left alone
  // it never settles, and its caller never reaches the finally that clears its loader.
  if (pendingAction.resolve || pendingAction.reject) {
    clearPendingAction(new Error('Superseded by a newer session action.'))
  }
  clearPendingTimeout()
  pendingAction.resolve = action.resolve
  pendingAction.reject = action.reject
  pendingAction.timeoutId = action.timeoutId
}

export function clearPendingAction(rejectReason?: any) {
  clearPendingTimeout()
  if (rejectReason && typeof pendingAction.reject === 'function') {
    console.warn(`Rejecting pending action: ${rejectReason.message || rejectReason}`)
    const reject = pendingAction.reject
    setTimeout(() => reject(rejectReason), 0)
  }
  pendingAction.resolve = null
  pendingAction.reject = null
}

export function hasPendingAction(): boolean {
  return pendingAction.resolve !== null || pendingAction.reject !== null
}

function handleMessage(raw: string) {
  let response: any
  try {
    response = JSON.parse(raw)
  } catch (e) {
    console.error('Failed to parse WebSocket message:', e, raw)
    clearPendingAction(new Error('Failed to parse WebSocket message'))
    return
  }

  if (!response || !response.status) {
    console.warn('Unrecognized WebSocket message format:', response)
    clearPendingAction(new Error('Unrecognized WebSocket message format'))
    return
  }

  const isSuccess = response.status >= HTTP_STATUS.OK && response.status < HTTP_STATUS.MULTIPLE_CHOICES
  if (isSuccess) {
    console.log('Websocket message received:', response)
    if (response.type === 'session' && onSessionMessage) {
      onSessionMessage(response.data, response.status)
      if (pendingAction.resolve) {
        pendingAction.resolve(response.data)
      }
      clearPendingAction()
      // Mouseflow page-view ping mirrors the action-flow's success path.
      const mfq = (window as any)._mfq
      if (mfq && response.data?.step) {
        mfq.push(['newPageView', `/${response.data.step_id}/${response.data.step_slug}`])
      }
    }
    return
  }

  console.error('WebSocket error response:', response.data)
  const error: any = new Error(response.data || 'WebSocket error')
  error.response = response
  clearPendingAction(error)
  onSocketError?.({ response })
}

export function connectWebSocket(): Promise<void> {
  if (isInteractivePreviewMode()) {
    return Promise.resolve()
  }
  return new Promise((resolve, reject) => {
    if (ws) {
      if (ws.readyState === WebSocket.OPEN) {
        resolve()
        return
      }
      if (ws.readyState === WebSocket.CONNECTING) {
        console.warn('WebSocket connection already in progress.')
        resolve()
        return
      }
      ws.close()
    }

    const wsEndpoint = environment.api.websocketEndpoint
    if (!wsEndpoint) {
      // Resolve silently so callers can still proceed via HTTP — the push
      // channel just won't be available for this environment.
      console.warn('WebSocket endpoint is not configured.')
      resolve()
      return
    }

    const token = getAuthToken() || ''
    if (!token) {
      console.error('Cannot connect WebSocket without a token.')
      reject(new Error('Cannot connect WebSocket without a token.'))
      return
    }

    let resolved = false
    const socket = new WebSocket(wsEndpoint, [`session-token.${token}`])

    socket.onopen = () => {
      if (!resolved) {
        resolved = true
        resolve()
      }
    }

    socket.onerror = event => {
      console.error('WebSocket error', event)
      if (!resolved) {
        resolved = true
        reject(new Error('WebSocket connection failed'))
      }
    }

    socket.onclose = event => {
      console.log('WebSocket closed', event.code, event.reason)
      if (!resolved) {
        resolved = true
        if (event.code === 1006) {
          reject(new Error('WebSocket connection failed: abnormal closure (1006)'))
        } else {
          reject(new Error(`WebSocket closed before connecting: ${event.code} ${event.reason}`))
        }
      }
      if (ws === socket) {
        ws = null
      }
    }

    socket.onmessage = event => handleMessage(event.data)
    ws = socket
  })
}

export function closeWebSocket() {
  if (!ws) {
    return
  }
  // Detach handlers so the close-during-pending path doesn't double-reject.
  ws.onopen = null
  ws.onmessage = null
  ws.onerror = null
  ws.onclose = null
  try {
    ws.close()
  } catch {
    /* socket may already be closing */
  }
  ws = null
}
